export type AssistanceType =
  | "True grant"
  | "Forgivable loan"
  | "Deferred loan"
  | "Lender credit"
  | "Mortgage program"
  | "Sweat-equity homeownership";

export interface GrantProgram {
  id: string;
  name: string;
  provider: string;
  group: "federal" | "national";
  assistance_type: AssistanceType;
  repayment_type: string;
  income_basis: string;
  geography_scope: string;
  first_time_rule: string;
  education_required: string;
  source_last_checked: string;
  detail: string;
}

const LAST_CHECKED = "August 2026";

export const GRANT_PROGRAMS: GrantProgram[] = [
  {
    id: "fhlbank",
    name: "FHLBank homeownership set-aside programs",
    provider: "Federal Home Loan Banks, through member lenders",
    group: "federal",
    assistance_type: "True grant",
    repayment_type:
      "Grant with a retention period, commonly five years; selling or refinancing early can trigger partial recapture",
    income_basis: "Typically at or below 80% of area median income (AMI)",
    geography_scope: "Regional — each of the 11 FHLBanks serves specific states",
    first_time_rule: "Usually required; commonly defined as no primary-residence ownership in the past three years",
    education_required: "Yes, homebuyer education is standard",
    source_last_checked: LAST_CHECKED,
    detail:
      "Funds are distributed by member banks and credit unions, not by the FHLBank directly, and they typically open in annual funding rounds that close when the money is committed. Because delivery runs through participating lenders, the only reliable way to access one is to ask lenders in your state whether they are an FHLBank member offering the set-aside this cycle.",
  },
  {
    id: "hcv-homeownership",
    name: "Housing Choice Voucher homeownership option",
    provider: "HUD, administered by local public housing agencies",
    group: "federal",
    assistance_type: "Mortgage program",
    repayment_type: "Not assistance funds — the voucher subsidizes monthly mortgage costs instead of rent",
    income_basis: "Existing voucher holders; PHA income and employment rules apply",
    geography_scope: "Local — only PHAs that have adopted the option participate",
    first_time_rule: "Generally required",
    education_required: "Yes, pre-purchase counseling is required",
    source_last_checked: LAST_CHECKED,
    detail:
      "This converts an existing rental voucher into monthly homeownership assistance. Adoption is optional for each housing authority, so availability varies sharply between neighboring counties. Minimum income and continuous-employment requirements usually apply, with exceptions for elderly and disabled households. Ask your PHA directly whether they run the program and whether the waiting list is open.",
  },
  {
    id: "usda-502-direct",
    name: "USDA Section 502 Direct Loan",
    provider: "USDA Rural Development",
    group: "federal",
    assistance_type: "Mortgage program",
    repayment_type: "A repayable mortgage; the payment subsidy portion is recaptured on sale or transfer",
    income_basis: "Low and very low income by county, generally below 80% AMI",
    geography_scope: "USDA-eligible rural areas only, checked by property address",
    first_time_rule: "Not strictly required, but you generally cannot own adequate housing already",
    education_required: "Yes, in most cases",
    source_last_checked: LAST_CHECKED,
    detail:
      "USDA lends directly rather than through a bank, with no down payment required and a payment subsidy that can push the effective interest rate very low for the lowest-income applicants. That subsidy is recaptured when you sell. Some places that feel suburban still qualify as rural under USDA maps, so check the exact address before ruling it out.",
  },
  {
    id: "home-funds",
    name: "HOME-funded local down payment assistance",
    provider: "State and local governments using federal HOME Investment Partnerships funds",
    group: "federal",
    assistance_type: "Deferred loan",
    repayment_type:
      "Most often a deferred or forgivable second lien with an affordability period; early sale can trigger repayment",
    income_basis: "At or below 80% AMI in nearly all cases",
    geography_scope: "City, county, or state jurisdiction that received the funds",
    first_time_rule: "Commonly required",
    education_required: "Usually yes, through a HUD-approved agency",
    source_last_checked: LAST_CHECKED,
    detail:
      "Most city and county down payment programs are HOME-funded underneath, which is why the terms rhyme from place to place: a silent second with a five to fifteen year affordability period, no monthly payment, and forgiveness once you have lived there long enough. The dollar amount and the affordability period are set locally, so two adjacent towns can offer very different deals.",
  },
  {
    id: "nhf",
    name: "National Homebuyers Fund style national DPA",
    provider: "Government-chartered housing entities, through approved lenders",
    group: "national",
    assistance_type: "Forgivable loan",
    repayment_type: "Depends on the option selected — a forgivable second or a repayable second",
    income_basis: "Program-set limits that are often higher than local caps; some options have none",
    geography_scope: "Multi-state, wherever an approved lender participates",
    first_time_rule: "Often not required",
    education_required: "Varies by option and lender",
    source_last_checked: LAST_CHECKED,
    detail:
      "These are marketed as grants but are usually structured as a second lien covering a percentage of the purchase price. The same sponsor may offer both a forgivable and a repayable version, and which one you are quoted depends on the lender and your credit profile. Ask which structure is on your loan estimate and get the forgiveness terms in writing.",
  },
  {
    id: "chenoa",
    name: "Chenoa Fund",
    provider: "CBC Mortgage Agency, a federally chartered tribal entity",
    group: "national",
    assistance_type: "Forgivable loan",
    repayment_type:
      "Forgivable after a set number of on-time payments on the income-limited option; repayable second on the no-income-limit option",
    income_basis: "One track uses an AMI cap; another has no borrower income limit",
    geography_scope: "Nationwide except New York, through approved lenders",
    first_time_rule: "Not required",
    education_required: "Yes, homebuyer education is required",
    source_last_checked: LAST_CHECKED,
    detail:
      "Pairs with an FHA first mortgage and typically covers the 3.5% down payment as a second lien. The forgivable track generally requires you to stay current for a defined period; miss that and the balance stays owed. Not every lender is approved to originate it, so a no from one lender is not a no from the program.",
  },
  {
    id: "naca",
    name: "NACA Best in America mortgage",
    provider: "Neighborhood Assistance Corporation of America",
    group: "national",
    assistance_type: "Mortgage program",
    repayment_type: "A mortgage — no down payment, no closing costs, no mortgage insurance, but fully repaid",
    income_basis: "No income limit for buyers purchasing in designated areas; priority rules apply by income and area",
    geography_scope: "Nationwide, through NACA offices and events",
    first_time_rule: "Not required, but you cannot own other property",
    education_required: "Yes — NACA counseling and workshops are mandatory",
    source_last_checked: LAST_CHECKED,
    detail:
      "Not a grant at all. NACA is a counseling-heavy path to a below-market mortgage with no down payment and no PMI, funded by participating lenders. The trade is time and documentation: the qualification process is thorough and can take months, and you must live in the home. For buyers with thin savings and steady income, the lifetime savings can dwarf a one-time grant.",
  },
  {
    id: "habitat",
    name: "Habitat for Humanity homeownership",
    provider: "Local Habitat affiliates",
    group: "national",
    assistance_type: "Sweat-equity homeownership",
    repayment_type: "An affordable mortgage held by the affiliate, often with a resale or shared-equity restriction",
    income_basis: "Typically 30% to 80% AMI, set by each affiliate",
    geography_scope: "Local affiliate service areas",
    first_time_rule: "Usually required in practice",
    education_required: "Yes, plus required sweat-equity hours",
    source_last_checked: LAST_CHECKED,
    detail:
      "Habitat sells homes it builds or rehabs to qualified households with an affordable mortgage, in exchange for volunteer sweat-equity hours. Selection is by application through the local affiliate, waitlists are common, and each affiliate sets its own income band and geography. Some affiliates also run repair programs for existing owners.",
  },
  {
    id: "bofa",
    name: "Bank of America Community Homeownership grants",
    provider: "Bank of America",
    group: "national",
    assistance_type: "True grant",
    repayment_type: "No repayment on the grant portion in most cases; confirm any retention period",
    income_basis: "Income limits apply in most markets; some products key off the property location instead",
    geography_scope: "Designated markets and census tracts",
    first_time_rule: "Commonly required for the down payment grant",
    education_required: "Yes, homebuyer education is generally required",
    source_last_checked: LAST_CHECKED,
    detail:
      "Bank grant programs pair a down payment grant with a closing-cost grant and are delivered only on that bank's own mortgage. Eligibility often keys off the property's census tract as much as your income, so run the exact street address before assuming you are out. Amounts and eligible markets are revised periodically.",
  },
  {
    id: "chase",
    name: "Chase Homebuyer Grant",
    provider: "JPMorgan Chase",
    group: "national",
    assistance_type: "Lender credit",
    repayment_type: "Applied at closing toward points, fees, or down payment; not repaid under normal terms",
    income_basis: "Often tied to the property's census tract rather than the borrower's income",
    geography_scope: "Eligible census tracts in designated markets",
    first_time_rule: "Not always required",
    education_required: "Encouraged; sometimes required for an additional credit",
    source_last_checked: LAST_CHECKED,
    detail:
      "This is a lender credit delivered at closing on a Chase mortgage, typically applied first to discount points to lower the rate and then to fees or down payment. Because eligibility follows the address, buyers who exceed income caps elsewhere sometimes still qualify. Check the property, not the neighborhood's reputation.",
  },
  {
    id: "wells",
    name: "Wells Fargo Homebuyer Access grant",
    provider: "Wells Fargo",
    group: "national",
    assistance_type: "True grant",
    repayment_type: "Grant toward the down payment; confirm whether any occupancy period applies",
    income_basis: "Generally at or below 120% AMI in eligible markets",
    geography_scope: "Selected metro markets",
    first_time_rule: "Varies by product",
    education_required: "Yes, homebuyer education is typically required",
    source_last_checked: LAST_CHECKED,
    detail:
      "Offered in a defined list of metropolitan markets and delivered only on that bank's mortgage, sometimes stackable with the bank's separate closing-cost credit. Market lists change, so ask whether your metro is currently included rather than relying on an article.",
  },
];

export interface RepaymentRow {
  label: string;
  plain: string;
  payBack: string;
  watchFor: string;
}

export const REPAYMENT_ROWS: RepaymentRow[] = [
  {
    label: "True grant",
    plain: "Money given to you at closing that is yours to keep.",
    payBack: "No, under normal circumstances",
    watchFor:
      "Many grants still carry a short occupancy or retention period. Selling or refinancing before it ends can mean paying some of it back.",
  },
  {
    label: "Forgivable loan",
    plain: "A silent second mortgage recorded on the home that is written off over time.",
    payBack: "Not if you stay long enough",
    watchFor:
      "Forgiveness periods commonly run five to fifteen years, and some forgive gradually while others forgive all at once at the end.",
  },
  {
    label: "Deferred loan",
    plain: "A real second loan with no monthly payment while you live there.",
    payBack: "Yes — the balance comes due later",
    watchFor:
      "Repayment is usually triggered by sale, refinance, or paying off the first mortgage. Ask whether interest accrues in the meantime.",
  },
  {
    label: "Lender credit",
    plain: "A credit the lender applies at closing toward points, fees, or your down payment.",
    payBack: "No, but it is tied to that lender's loan",
    watchFor:
      "You have to use that lender's mortgage to get it, so compare the rate and fees against outside quotes before assuming the credit wins.",
  },
];